import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { FormControl, Validators } from '@angular/forms';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';

@Component({
  selector: 'app-add-projects',
  template: `
    <div class="add-projects-page">
      <div class="pagehead">
        <h1>Add projects to your solution</h1>
        <p>Create a new project or copy existing ones into this version.</p>
      </div>

      <div class="panel">
        <div class="seg">
          <button
            type="button"
            [class.active]="newProjectFlag"
            (click)="newProject()"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            New
          </button>
          <button
            type="button"
            #exist
            [class.active]="existingProject"
            (click)="addExistingProject()"
          >
            Existing
          </button>
        </div>

        <div *ngIf="newProjectFlag">
          <div class="fld">
            <label>Project name</label>
            <mat-form-field class="boxed-field">
              <input
                matInput
                type="text"
                placeholder="Enter project name"
                [(ngModel)]="projectName"
              />
            </mat-form-field>
          </div>

          <label
            class="check-row"
            [class.on]="refreshData"
            (click)="refreshData = !refreshData"
          >
            <span class="cbox" [class.on]="refreshData">
              <svg
                *ngIf="refreshData"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <input
              type="checkbox"
              class="check-input"
              [(ngModel)]="refreshData"
            />
            Refresh data before a project runs
          </label>

          <hr class="divider" />

          <button
            type="button"
            class="btn-primary"
            [disabled]="projectName.trim() == ''"
            (click)="createNewProject()"
          >
            Create
          </button>
        </div>

        <div *ngIf="existingProject">
          <div class="fld">
            <label>Select projects</label>
            <mat-form-field class="boxed-field">
              <mat-select [formControl]="existingProjectSelected" multiple>
                <mat-select-trigger>
                  {{existingProjectSelected.value?.[0] || ''}}
                  <span
                    *ngIf="(existingProjectSelected.value?.length || 0) > 1"
                    class="example-additional-selection"
                  >
                    (+{{ (existingProjectSelected.value?.length || 0) - 1 }}
                    {{
                      existingProjectSelected.value?.length === 2
                        ? 'other'
                        : 'others'
                    }})
                  </span>
                </mat-select-trigger>
                <div *ngFor="let solution of optionList | keyvalue" class="ml-4">
                  {{ solution.key }}:
                  <div
                    *ngFor="let version of solution.value | keyvalue"
                    class="ml-6"
                  >
                    {{ version.key }}:
                    <mat-option
                      *ngFor="let project of version.value"
                      [value]="project['project_id']"
                      class="text-sm ml-6"
                    >
                      {{ project['project_name'] }}
                    </mat-option>
                  </div>
                </div>
              </mat-select>
            </mat-form-field>
          </div>

          <hr class="divider" />

          <button
            type="button"
            class="btn-primary"
            [disabled]="!existingProjectSelected.valid"
            (click)="copyExistingProject()"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./add-projects.component.scss'],
})
export class AddProjectsComponent implements OnInit {
  currentSelectedSolution: any;
  currentSelectedVersion: any;
  refreshData = false;
  projectName = '';
  newProjectFlag: boolean = false;
  existingProject: boolean = false;
  optionList: { [key: string]: { [key: string]: any } } = {};
  existingProjectSelected = new FormControl('', Validators.required);
  constructor(
    private solutionServices: SolutionsAppService,
    private snackBar: SharedFeaturesService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.currentSelectedSolution =
      this.route.snapshot.paramMap.get('solutionId');
    this.currentSelectedVersion = this.route.snapshot.paramMap.get('versionId');
    this.solutionServices.getAvailableSolution().subscribe({
      next: (response: any) => {
        let temp = response;
        temp = temp.filter(
          (obj: any) => obj['solution_id'] !== this.currentSelectedSolution
        );
        for (const solution of temp) {
          this.solutionServices.getAvailableVersion(solution).subscribe({
            next: (response: any) => {
              for (const version of response) {
                this.getProjects(solution, version);
              }
            },
          });
        }
      },
    });
  }

  getProjects(solution: any, version: any) {
    this.solutionServices.getProjectsInVersion(solution, version).subscribe({
      next: (response: any) => {
        if (response.length > 0) {
          if (!this.optionList[solution['solution_name']]) {
            this.optionList[solution['solution_name']] = {};
          }

          this.optionList[solution['solution_name']][version['version_name']] =
            response;
        }
      },
      error: (error: any) => {
        console.error('Error fetching projects:', error);
      },
    });
  }

  createNewProject() {
    let temp = {
      project_name: this.projectName.trim().toLowerCase(),
      version_id: this.currentSelectedVersion,
      refresh_data_flag: this.refreshData,
    };
    this.solutionServices
      .createProject(
        { solution_id: this.currentSelectedSolution },
        { version_id: this.currentSelectedVersion },
        temp
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            'project created successfully',
            'Close',
            'success-snackbar'
          );
          this.router.navigate([
            '/home/manage-solutions/mySolutions',
            this.currentSelectedSolution,
            this.currentSelectedVersion,
            response?.project_id,
            'configurePipeline',
          ]);
        },
        error: (error) => {
          this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
        },
      });
  }

  copyExistingProject() {
    let temp = {
      project_ids_to_copy: this.existingProjectSelected.value,
    };
    this.solutionServices
      .copyProjects(
        { solution_id: this.currentSelectedSolution },
        { version_id: this.currentSelectedVersion },
        temp
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(
            response['msg'],
            'Close',
            'success-snackbar'
          );
          this.router.navigate([
            '/home/manage-solutions/mySolutions/' +
              this.currentSelectedSolution,
          ]);
        },
        error: (error) => {
          this.snackBar.openSnackBar(error.message, 'Close', 'error-snackbar');
        },
      });
  }

  newProject() {
    this.newProjectFlag = !this.newProjectFlag;
    this.existingProject = false;
  }

  addExistingProject() {
    this.existingProject = !this.existingProject;
    this.newProjectFlag = false;
  }
}
